import React, { useEffect, useMemo, useState } from "react";
import { getAhaCatalogAdmin, getStandardCourseAhaProductLinks, linkStandardCourseAhaProduct } from "../../../../api/apiClient";
import { Check, Modal, Notice, StatePanel } from "../../ui/AdminUi";
import { I } from "../../ui/icons";
import { errorText } from "../common";
import { STATE, productState } from "./model";

/** Choose AHA products to link to a course. Products that belong to another course are shown, locked, with that course. */
export default function AhaProductPicker({ courseId, courseTitle, onClose, onLinked }) {
  const [products, setProducts] = useState(null);
  const [owners, setOwners] = useState({});
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getAhaCatalogAdmin(), getStandardCourseAhaProductLinks()])
      .then(([catalog, links]) => {
        if (cancelled) return;
        setProducts(catalog?.products || []);
        setOwners(links || {});
      })
      .catch((e) => { if (!cancelled) { setProducts([]); setError(errorText(e, "The AHA catalog could not be loaded.")); } });
    return () => { cancelled = true; };
  }, []);

  const list = useMemo(() => (products || [])
    .filter((p) => `${p.name} ${p.key} ${p.environment?.productNumber || ""}`.toLowerCase().includes(query.trim().toLowerCase()))
    .filter((p) => owners[p.key]?.id !== courseId)
    .sort((a, b) => Number(Boolean(owners[a.key])) - Number(Boolean(owners[b.key])) || a.name.localeCompare(b.name)), [products, owners, query, courseId]);

  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  const link = async () => {
    setSaving(true);
    setError(null);
    const failed = [];
    for (const key of selected) {
      try {
        // Sequential on purpose: each link is audited, and a product taken by another admin meanwhile is refused.
        // eslint-disable-next-line no-await-in-loop
        await linkStandardCourseAhaProduct(courseId, key);
      } catch (e) {
        failed.push(`${key}: ${errorText(e, "could not be linked")}`);
      }
    }
    setSaving(false);
    if (failed.length) {
      setError(failed.join(" "));
      setSelected((s) => s.filter((k) => failed.some((f) => f.startsWith(`${k}:`))));
      onLinked(selected.length - failed.length);
      return;
    }
    onLinked(selected.length);
    onClose();
  };

  return (
    <Modal wide title="Link AHA product" sub={`To ${courseTitle}. A course can link several products.`} onClose={() => !saving && onClose()} labelId="aha-picker-title"
      footer={(
        <>
          <span className="note">Linking does not change any environment status.</span>
          <button type="button" className="b ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="b pri" disabled={!selected.length || saving} onClick={link}>
            <I.link s={13} />{saving ? "Linking…" : `Link ${selected.length > 1 ? `${selected.length} products` : "product"}`}
          </button>
        </>
      )}
    >
      {error && <Notice kind="bad">{error}</Notice>}
      <div className="srch" style={{ maxWidth: "none" }}>
        <I.search s={15} />
        <input placeholder="Search name, key or AHA number" aria-label="Search products" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      </div>
      <div className="panel" style={{ overflow: "auto", maxHeight: 320 }}>
        {products === null ? <StatePanel kind="loading" what="AHA products" />
          : list.length === 0 ? <div className="state" style={{ padding: 28 }}><b>No products match</b></div>
            : list.map((p, i) => {
              const owner = owners[p.key];
              const state = STATE[productState(p.environment || {})];
              return (
                <div key={p.key} className="pk-row" data-testid={`picker-row-${p.key}`} style={{ borderTop: i ? "1px solid var(--ga-line)" : 0, opacity: owner ? 0.6 : 1 }}>
                  {owner ? <span className="sub" style={{ width: 18 }}><I.lock s={14} /></span> : <Check on={selected.includes(p.key)} label={p.name} onClick={() => toggle(p.key)} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ fontWeight: 600 }}>{p.name}{p.edition ? ` (${p.edition})` : ""}</b>
                    <div style={{ marginTop: 3, display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {p.environment?.productNumber && <span className="code">{p.environment.productNumber}</span>}
                      <span className="mono sub" style={{ fontSize: 11.5 }}>{p.key}</span>
                    </div>
                  </div>
                  {owner ? <span className="sub" style={{ fontSize: 12.5 }}>Linked to {owner.title}</span> : <span className={`chip ${state.tone}`}>{state.short}</span>}
                </div>
              );
            })}
      </div>
    </Modal>
  );
}
